import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    View,
    colors,
    Heading,
    Text,
    css,
    Button,
    Icon,
} from '@fairygodboss/ui';
import window from 'global/window';
import clip from 'text-clipper';
import { GridColumnWrapper } from './GridColumnWrapper';
import { useEventRegistration } from '../EventRegistrationContext';
import { buildImagePath } from '../../../JobComponents/Company/Image/logo';
import { isOverflown } from './helpers';

export const ShowMoreButton = ({ onPress }) => (
    <Button
        onPress={onPress}
        css={css({
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-block',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '16px',
            height: '24px',
            padding: '0 4px',
            textDecoration: 'none !important',
            color: colors.primary[60],
            margin: 0,
            '&:hover': {
                color: `${colors.primary[80]}`,
                background: 'transparent',
            },
            '&:active': {
                outline: 'none',
                background: 'transparent',
            },
        })}
        variant="primaryText"
    >
        Show more
    </Button>
);

export const ShowMoreMobileButton = ({ active, onPress }) => {
    return (
        <Button
            size="large"
            variant="primaryText"
            px="20px"
            py="10px"
            rightIcon={
                <Icon
                    outlineColor={colors.primary[60]}
                    variant={active ? 'chevron_up_16' : 'chevron_down_16'}
                />
            }
            onPress={onPress}
        >
            Show {active ? 'less' : 'more'}
        </Button>
    );
};

const MobileSpeaker = ({ speaker }) => {
    const { image, name, shortBio, bio } = speaker;
    const [collapsed, setCollapsed] = useState(true);
    const [hasOverflow, setHasOverflow] = useState(false);
    const textRef = useRef();

    const onShowMoreClick = () => {
        setCollapsed(!collapsed);
    };

    const src = buildImagePath(image.src, 'img', {
        img: 'c_scale,g_center,r_max,q_auto,fl_lossy,w_270,h_270',
    });

    useEffect(() => {
        if (textRef?.current) setHasOverflow(isOverflown(textRef.current));
    }, [textRef]);

    return (
        <View
            pb="48px"
            css={{
                button: {
                    backgroundColor: colors.secondary[5],
                },
            }}
        >
            <View display="flex" justifyContent="center" pb="24px">
                <View
                    width="156px"
                    height="156px"
                    borderRadius="50%"
                    as="img"
                    src={src}
                    alt={`${name}, ${image.alt}`}
                />
            </View>
            <Heading pb="4px" variant="headingSm" as="h3">
                {name}
            </Heading>
            <Heading pb="16px" variant="headingXs" as="h4">
                {shortBio}
            </Heading>
            <View
                ref={textRef}
                fontSize="16px"
                fontWeight="400px"
                lineHeight="26px"
                maxHeight={collapsed ? '182px' : 'auto'}
                overflow="hidden"
                css={{
                    WebkitMaskImage:
                        collapsed && hasOverflow
                            ? 'linear-gradient(180deg, #000 60%, transparent)'
                            : 'none',
                }}
            >
                {bio}
            </View>
            {hasOverflow ? (
                <View display="flex" justifyContent="center">
                    <ShowMoreMobileButton
                        active={!collapsed}
                        onPress={onShowMoreClick}
                    />
                </View>
            ) : (
                true
            )}
        </View>
    );
};

const DesktopSpeaker = ({ speaker, invert }) => {
    const { image, name, shortBio, bio } = speaker;
    const [collapsed, setCollapsed] = useState(true);
    const [charLimit, setCharLimit] = useState(550);

    const handleResize = useCallback(() => {
        const _isSm = window.innerWidth < 905;
        if (_isSm && charLimit !== 400) {
            setCharLimit(400);
        } else if (!_isSm && charLimit !== 550) {
            setCharLimit(500);
        }
    }, [charLimit]);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const onShowMore = useCallback(
        (e) => {
            e.preventDefault();
            setCollapsed(!collapsed);
        },
        [collapsed]
    );

    const src = buildImagePath(image.src, 'img', {
        img: 'c_scale,g_center,r_max,q_auto,fl_lossy,w_270,h_270',
    });

    const clippedBio = clip(bio, charLimit, {
        indicator: `...`,
    });

    return (
        <View
            display="flex"
            flexDirection={{ _: 'column', md: invert ? 'row-reverse' : 'row' }}
            pb={{ _: '48px', lg: '64px' }}
        >
            <View display="flex">
                <View
                    mr={{ _: '24px', md: invert ? '0px' : '24px' }}
                    ml={{ _: 'column', md: invert ? '24px' : '0px' }}
                    width={{ sm: '130px', md: '197px', lg: '270px' }}
                    height={{ sm: '130px', md: '197px', lg: '270px' }}
                    borderRadius="50%"
                    as="img"
                    src={src}
                    alt={`${name}, ${image.alt}`}
                />
                <View
                    display={{ _: 'flex', md: 'none' }}
                    flexDirection="column"
                >
                    <Heading variant="headingMd" as="h3">
                        {name}
                    </Heading>
                    <Heading variant="headingXs" as="h4">
                        {shortBio}
                    </Heading>
                </View>
            </View>

            <View>
                <Heading
                    as="h4"
                    display={{ _: 'none', md: 'inherit' }}
                    variant={{ sm: 'headingSm', md: 'headingMd' }}
                >
                    {name}
                </Heading>
                <Heading
                    as="h5"
                    display={{ _: 'none', md: 'inherit' }}
                    variant="headingXs"
                >
                    {shortBio}
                </Heading>
                <Text pt="16px" variant="bodyMd">
                    {collapsed ? clippedBio : bio}
                    {collapsed && bio.length > charLimit ? (
                        <ShowMoreButton onPress={onShowMore} />
                    ) : null}
                </Text>
            </View>
        </View>
    );
};

export const SpeakerSection = () => {
    const { event } = useEventRegistration();
    const { speakers } = event;

    return speakers.length ? (
        <GridColumnWrapper
            backgroundColor={colors.secondary[5]}
            color={colors.neutral[100]}
            gridAlignment="center"
            px={{ _: '16px', sm: '0px', md: '24px', lg: '0px' }}
            pb={{ _: '0px', md: '16px', lg: '0px' }}
            gridColumnSpacing={{ xs: 12, sm: 9, md: 10, lg: 10 }}
        >
            <Heading
                pb="40px"
                fontWeight="700"
                variant={{ _: 'headingSm', sm: 'headingMd', md: 'headingLg' }}
                pt={{ _: '32px', md: '56px' }}
                as="h2"
            >
                Meet the speaker{speakers.length > 1 ? 's' : ''}
            </Heading>
            <View display={{ _: 'none', sm: 'inherit' }}>
                {speakers.map((s, i) => (
                    <DesktopSpeaker
                        speaker={s}
                        invert={!!(i % 2)}
                        key={s?.name?.split(' ')?.join('-')}
                    />
                ))}
            </View>
            <View display={{ _: 'inherit', sm: 'none' }}>
                {speakers.map((s) => (
                    <MobileSpeaker
                        speaker={s}
                        key={s?.name?.split(' ')?.join('-')}
                    />
                ))}
            </View>
        </GridColumnWrapper>
    ) : null;
};

SpeakerSection.displayName = 'SpeakerSection';
